import { Component } from 'react';
import { Redirect, Route, Switch, matchPath } from 'react-router-dom';
import { RouteComponentProps } from 'types';
import { getInMobileNative } from 'selectors/appSelector';
import { isBlackListedLocation } from '@tock/shared/utils/scroll';
import { loadable } from 'utils/loadable';
import { parse } from '@tock/query-string';
import BusinessPreviewSecondaryHeader from 'components/header/BusinessPreviewSecondaryHeader';
import Footer from 'components/footer/Footer';
import NotFoundRoute from 'routes/NotFoundRoute';
import OmakaseHeader from 'components/header/OmakaseHeader';
// testing not code splitting this route for faster performance
import { IWithContextProps, IWithContextRef, withContext } from '@tock/coretypes';
import { WithIsSmallScreenProps, withIsSmallScreen } from 'utils/withIsSmallScreen';
import SearchRoute from 'routes/www/SearchRoute';
import _ from 'lodash';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';
import searchBarSuggestionsSelector from 'selectors/searchBarSuggestionsSelector';

const HelpRoute = loadable(() => import('routes/business/HelpRoute'));
const ForgotPasswordRoute = loadable(() => import('routes/unprotected/ForgotPasswordRoute'));
const ResetPasswordRoute = loadable(() => import('routes/unprotected/ResetPasswordRoute'));
const SigninRoute = loadable(() => import('routes/unprotected/SigninRoute'));
const ProfileHomeRoute = loadable(() => import('routes/account/ProfileHomeRoute'));
const PlaylistRoute = loadable(() => import('routes/playlist/PlaylistRoute'));
const PartnerRoute = loadable(() => import('routes/account/PartnerRoute'));
const PrivacyRoute = loadable(() => import('routes/legal/PrivacyRoute'));
const PrivacySpecificRoute = loadable(() => import('routes/legal/PrivacySpecificRoute'));
const MerchantDPARoute = loadable(() => import('routes/legal/MerchantDPARoute'));
const TermsRoute = loadable(() => import('routes/legal/TermsRoute'));
const MerchantRoute = loadable(() => import('routes/legal/MerchantRoute'));
const AffiliateRoute = loadable(() => import('routes/legal/AffiliateRoute'));
const GiftCardTermsRoute = loadable(() => import('routes/legal/GiftCardTermsRoute'));
const ConciergeTermsRoute = loadable(() => import('routes/legal/ConciergeTermsRoute'));
const CookieRoute = loadable(() => import('routes/legal/CookieRoute'));
const AppRoute = loadable(() => import('routes/www/AppRoute'));
const RatesRoute = loadable(() => import('routes/www/RatesRoute'));
const WidgetBuilderRoute = loadable(() => import('routes/www/widget/WidgetBuilderRoute'));
const WidgetPluginRoute = loadable(() => import('routes/www/widget/WidgetPluginRoute'));
const ContinuePurchaseRoute = loadable(
  () => import('routes/business/handsell-flow/ContinuePurchaseRoute')
);
const ContinueTableSideRoute = loadable(
  () => import('routes/business/tableside-flow/ContinueTableSideRoute')
);
const ContinueWalkInWaitlistRoute = loadable(
  () => import('routes/business/ContinueWalkInWaitlistRoute')
);
const GiftCardRoute = loadable(() => import('routes/gift/GiftCardRoute'));
const GenericSearchRoute = loadable(() => import('routes/www/GenericSearchRoute'));
const CollectionPageRoute = loadable(() => import('routes/www/CollectionPageRoute'));
const WineryRoute = loadable(() => import('routes/www/WineryRoute'));
const BusinessRoute = loadable(() => import('routes/business/BusinessRoute'));

const CustomConsumerPageRoute = loadable(() => import('routes/www/CustomConsumerPageRoute'));

class StandardHeaderRoute extends Component<
  ConnectProps & IWithContextProps & WithIsSmallScreenProps & RouteComponentProps<{}>
> {
  getShowJoinTockPartnerLink(): boolean {
    const { app, inMobileNative } = this.props;
    if (inMobileNative) {
      return false;
    }
    return app?.config?.domain?.context === 'WWW';
  }

  render() {
    const { inMobileNative, location } = this.props;

    const homePageMatch = matchPath(location.pathname, {
      path: '/',
      exact: true,
    });
    const showPartnerLink = this.getShowJoinTockPartnerLink() && !_.isNil(homePageMatch);
    const showPreviewWarning = !_.isNil(parse(location.search).previewMode) && !showPartnerLink;

    const showFooter = !isBlackListedLocation(location, [
      { path: '/checkout/' },
      { path: '/login' },
      { path: '/signup' },
      { path: '/reset' },
      { path: '/forgot-password' },
      { path: '/forgot' },
      { path: '/:type(city|group)/:value' },
      { path: '/:type(togo)' },
      { path: '/c/:collection' },
    ]);

    let banner;

    if (showPreviewWarning) {
      banner = <BusinessPreviewSecondaryHeader location={location} />;
    }

    return (
      <>
        <div id="skip">
          <a href="#maincontent">Skip to main content</a>
        </div>
        {banner && <aside>{banner}</aside>}
        <OmakaseHeader />
        <main id="maincontent">
          <Switch location={location}>
            <Route exact path="/" component={CustomConsumerPageRoute} />
            <Redirect from="/forgot" to={{ ...location, pathname: '/forgot-password' }} />
            <Redirect from="/signin" to={{ ...location, pathname: '/signup' }} />
            <Route path="/:loginOrSignup(login|signup)" component={SigninRoute} />
            <Route path="/forgot-password" component={ForgotPasswordRoute} />
            <Route path="/reset" component={ResetPasswordRoute} />
            <Route path="/privacy" component={PrivacyRoute} />
            <Route path="/privacy-specific" component={PrivacySpecificRoute} />
            <Route path="/merchant-dpa" component={MerchantDPARoute} />
            <Route path="/merchant-terms" component={MerchantRoute} />
            <Route path="/affiliate-terms" component={AffiliateRoute} />
            <Route path="/concierge-terms" component={ConciergeTermsRoute} />
            <Route path="/cookie-policy" component={CookieRoute} />
            <Route path="/help" component={HelpRoute} />,
            <Route path="/rates" component={RatesRoute} />
            <Route path="/terms" component={TermsRoute} />
            <Route path="/gift-card-terms" component={GiftCardTermsRoute} />
            <Route path="/gift" component={GiftCardRoute} />
            <Route path="/widget-builder" component={WidgetBuilderRoute} />
            <Route path="/widget-plugin" component={WidgetPluginRoute} />
            <Route path="/app" component={AppRoute} />
            <Route path="/pay" component={ContinueTableSideRoute} />
            <Route path="/order" component={ContinueTableSideRoute} />
            <Route path="/book-by-text" component={ContinuePurchaseRoute} />
            <Route path="/walkin-waitlist" component={ContinueWalkInWaitlistRoute} />
            <Route path="/:type(city|group|togo)" component={SearchRoute} />
            <Route path="/c/:collection" component={CollectionPageRoute} />
            <Route path="/collections/wineries" component={WineryRoute} />
            <Route path="/search" component={GenericSearchRoute} />
            <Route path="/collections/:slug*" component={CustomConsumerPageRoute} />
            <Route path="/concierge" component={PartnerRoute} />
            <Route path="/profile" component={ProfileHomeRoute} />
            <Route path="/playlist/:playlistId" component={PlaylistRoute} />
            <Route path="/:businessDomain" component={BusinessRoute} />
            <Route path="*" component={NotFoundRoute} />
          </Switch>
        </main>

        {showFooter && <Footer isMobileNative={inMobileNative} />}
      </>
    );
  }
}

const mapStateToProps = (state: StoreType) => {
  return {
    appContext: state.app.appContext,
    consumerPage: state.consumerPage,
    currency: state.app.currency,
    inMobileNative: getInMobileNative(state),
    navigation: state.navigation,
    patron: state.app.patronProfile?.patron ?? undefined,
    search: state.search,
    searchBarSuggestions: searchBarSuggestionsSelector(state),
    ui: state.ui,
  };
};

interface ConnectProps
  extends DefaultConnectProps,
    Pick<StoreType, 'consumerPage' | 'navigation' | 'search' | 'ui'> {
  appContext: StoreType['app']['appContext'];
  inMobileNative: boolean;
}

export default connect<
  IWithContextRef & WithIsSmallScreenProps & RouteComponentProps<{}>,
  ConnectProps
>(withContext(withIsSmallScreen(StandardHeaderRoute)), mapStateToProps);
